import React from 'react';
import { isAccountAuthConfigured, supabase } from './supabaseClient';

const guestAccount = {
  configured: isAccountAuthConfigured,
  loading: false,
  session: null,
  user: null,
  signInWithGoogle: async () => {},
  sendMagicLink: async () => {},
  signOut: async () => {},
};

const AccountContext = React.createContext(guestAccount);

function redirectUrl() {
  if (typeof window === 'undefined') return undefined;
  return `${window.location.origin}${window.location.pathname || '/'}`;
}

export function AccountProvider({ children }) {
  const [session, setSession] = React.useState(null);
  const [loading, setLoading] = React.useState(isAccountAuthConfigured);

  React.useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return undefined;
    }

    let active = true;
    supabase.auth.getSession()
      .then(({ data }) => {
        if (!active) return;
        setSession(data.session || null);
      })
      .catch(() => {
        if (!active) return;
        setSession(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (!active) return;
      setSession(nextSession || null);
      setLoading(false);
    });

    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, []);

  const signInWithGoogle = React.useCallback(async () => {
    if (!supabase) throw new Error('Account sign-in is not configured in this build.');
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: redirectUrl() },
    });
    if (error) throw error;
  }, []);

  const sendMagicLink = React.useCallback(async (email) => {
    if (!supabase) throw new Error('Account sign-in is not configured in this build.');
    const normalized = String(email || '').trim().toLowerCase();
    if (!normalized || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)) {
      throw new Error('Enter a valid email address.');
    }
    const { error } = await supabase.auth.signInWithOtp({
      email: normalized,
      options: {
        shouldCreateUser: true,
        emailRedirectTo: redirectUrl(),
      },
    });
    if (error) throw error;
  }, []);

  const signOut = React.useCallback(async ({ ignoreRemoteError = false } = {}) => {
    if (!supabase) return;
    try {
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      if (error && !ignoreRemoteError) throw error;
    } catch (error) {
      if (!ignoreRemoteError) throw error;
    } finally {
      // A deleted or expired account can no longer complete a remote logout.
      // Always clear the browser session so stale account UI cannot remain.
      setSession(null);
    }
  }, []);

  const value = React.useMemo(() => ({
    configured: isAccountAuthConfigured,
    loading,
    session,
    user: session?.user || null,
    signInWithGoogle,
    sendMagicLink,
    signOut,
  }), [loading, session, signInWithGoogle, sendMagicLink, signOut]);

  return (
    <AccountContext.Provider value={value}>
      {children}
    </AccountContext.Provider>
  );
}

export function useAccount() {
  return React.useContext(AccountContext);
}
